import React, { type FC, type SVGProps } from 'react';
import { ChevronRight, ExternalLink, Globe, UserRound } from 'lucide-react';
import { BRAND_TIFFANY_BLUE } from '../../constants/branding';
import { useI18n } from '../../i18n';

type StaffSelectionCardSnsLink = {
  platform: string;
  url: string;
};

type StaffSelectionCardProps = {
  name: string;
  subLabel?: string | null;
  roleName?: string | null;
  bio?: string | null;
  iconUrl?: string | null;
  snsLinks?: StaffSelectionCardSnsLink[];
  isSelected: boolean;
  onSelect: () => void;
};

type SocialStyle = {
  label: string;
  Icon: FC<SVGProps<SVGSVGElement>>;
  buttonClassName: string;
  iconWrapClassName: string;
  iconClassName?: string;
};

const InstagramIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <rect x="3.25" y="3.25" width="17.5" height="17.5" rx="5.25" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="12" cy="12" r="4.2" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="17.2" cy="6.8" r="1.15" fill="currentColor" />
  </svg>
);

const TikTokIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path
      fill="currentColor"
      d="M14.88 3.5c.28 1.82 1.25 3.3 3.14 4.07.56.23 1.16.35 1.79.37v2.8a8.52 8.52 0 0 1-3.77-.93v5.09a5.92 5.92 0 1 1-5.09-5.86v2.86a3.11 3.11 0 1 0 2.12 2.95V3.5h1.81Z"
    />
  </svg>
);

const XIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path
      fill="currentColor"
      d="M18.91 3.75h2.54l-5.56 6.35L22.43 20h-5.12l-4-5.23L8.73 20H6.18l5.94-6.79L1.87 3.75h5.25l3.62 4.77 4.17-4.77Zm-.9 14.72h1.41L6.36 5.2H4.84l13.17 13.27Z"
    />
  </svg>
);

const YoutubeIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path
      fill="currentColor"
      d="M21.07 7.18a2.9 2.9 0 0 0-2.03-2.06C17.26 4.62 12 4.62 12 4.62s-5.26 0-7.04.5A2.9 2.9 0 0 0 2.93 7.18c-.5 1.8-.5 4.82-.5 4.82s0 3.02.5 4.82a2.9 2.9 0 0 0 2.03 2.06c1.78.5 7.04.5 7.04.5s5.26 0 7.04-.5a2.9 2.9 0 0 0 2.03-2.06c.5-1.8.5-4.82.5-4.82s0-3.02-.5-4.82ZM10.28 15.2V8.8l5.62 3.2-5.62 3.2Z"
    />
  </svg>
);

const FacebookIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path
      fill="currentColor"
      d="M13.39 20.5v-6.62h2.22l.33-2.58h-2.55V9.66c0-.75.2-1.26 1.28-1.26h1.37V6.09c-.24-.03-1.05-.09-2-.09-1.98 0-3.33 1.2-3.33 3.42v1.88H8.5v2.58h2.2v6.62h2.69Z"
    />
  </svg>
);

const LineIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path
      fill="currentColor"
      d="M12 4.1c-4.85 0-8.8 3.16-8.8 7.04 0 3.48 3.12 6.4 7.33 6.95.29.06.69.18.79.42.09.22.06.56.03.78l-.13.75c-.04.22-.18.87.76.48.95-.4 5.14-3.03 7.01-5.18 1.29-1.4 1.91-2.81 1.91-4.2 0-3.88-3.95-7.04-8.9-7.04Zm-4.56 8.87a.5.5 0 0 1-.5-.5V9.56a.5.5 0 0 1 1 0v2.41h1.33a.5.5 0 0 1 0 1H7.44Zm3.27 0a.5.5 0 0 1-.5-.5V9.56a.5.5 0 0 1 1 0v2.91a.5.5 0 0 1-.5.5Zm4.26 0a.5.5 0 0 1-.4-.2l-1.62-2.18v1.88a.5.5 0 0 1-1 0V9.56a.5.5 0 0 1 .9-.3l1.62 2.18V9.56a.5.5 0 0 1 1 0v2.91a.5.5 0 0 1-.5.5Zm2.88-1h1.33a.5.5 0 0 1 0 1h-1.83a.5.5 0 0 1-.5-.5V9.56a.5.5 0 0 1 .5-.5h1.83a.5.5 0 0 1 0 1h-1.33v.45h1.33a.5.5 0 0 1 0 1h-1.33v.46Z"
    />
  </svg>
);

const WebsiteIcon: FC<SVGProps<SVGSVGElement>> = (props) => <Globe {...props} />;

const SOCIAL_STYLE_MAP: Record<string, SocialStyle> = {
  instagram: {
    label: 'Instagram',
    Icon: InstagramIcon,
    buttonClassName:
      'border-rose-200 bg-gradient-to-r from-fuchsia-600 via-rose-500 to-orange-400 text-white hover:from-fuchsia-500 hover:via-rose-400 hover:to-orange-300',
    iconWrapClassName: 'bg-white/18 text-white',
    iconClassName: 'h-4 w-4',
  },
  tiktok: {
    label: 'TikTok',
    Icon: TikTokIcon,
    buttonClassName:
      'border-slate-800 bg-[#111111] text-white hover:bg-[#1d1d1d]',
    iconWrapClassName: 'bg-white/10 text-[#5ffbf1]',
    iconClassName: 'h-4 w-4',
  },
  x: {
    label: 'X',
    Icon: XIcon,
    buttonClassName:
      'border-slate-800 bg-black text-white hover:bg-slate-900',
    iconWrapClassName: 'bg-white/10 text-white',
    iconClassName: 'h-4 w-4',
  },
  youtube: {
    label: 'YouTube',
    Icon: YoutubeIcon,
    buttonClassName:
      'border-red-200 bg-[#ff0033] text-white hover:bg-[#e0002d]',
    iconWrapClassName: 'bg-white/15 text-white',
    iconClassName: 'h-4 w-4',
  },
  facebook: {
    label: 'Facebook',
    Icon: FacebookIcon,
    buttonClassName:
      'border-blue-200 bg-[#1877f2] text-white hover:bg-[#1667d9]',
    iconWrapClassName: 'bg-white/15 text-white',
    iconClassName: 'h-4 w-4',
  },
  line: {
    label: 'LINE',
    Icon: LineIcon,
    buttonClassName:
      'border-emerald-200 bg-[#06c755] text-white hover:bg-[#05b44d]',
    iconWrapClassName: 'bg-white/15 text-white',
    iconClassName: 'h-4 w-4',
  },
  website: {
    label: 'Website',
    Icon: WebsiteIcon,
    buttonClassName:
      'border-cyan-200 bg-cyan-500 text-white hover:bg-cyan-600',
    iconWrapClassName: 'bg-white/15 text-white',
    iconClassName: 'h-4 w-4',
  },
  other: {
    label: 'SNS',
    Icon: WebsiteIcon,
    buttonClassName:
      'border-amber-200 bg-amber-500 text-white hover:bg-amber-600',
    iconWrapClassName: 'bg-white/15 text-white',
    iconClassName: 'h-4 w-4',
  },
};

const StaffSelectionCard: React.FC<StaffSelectionCardProps> = ({
  name,
  subLabel,
  roleName,
  bio,
  iconUrl,
  snsLinks = [],
  isSelected,
  onSelect,
}) => {
  const { t } = useI18n();
  const displayInitial = name?.trim()?.[0] || '?';
  const normalizedBio = bio?.trim();
  const visibleSnsLinks = snsLinks.filter((link) => Boolean(link.url?.trim()));

  return (
    <div
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
      role="button"
      tabIndex={0}
      className={[
        'w-full rounded-[32px] border px-5 py-5 text-left shadow-sm transition',
        'min-h-[196px]',
        isSelected
          ? 'border-[#87dfdb] bg-[linear-gradient(180deg,rgba(223,248,246,0.96),rgba(255,255,255,1))] text-slate-900 shadow-[0_16px_34px_rgba(10,186,181,0.14)]'
          : 'border-slate-200 bg-white text-slate-800 hover:border-[#c4ece9] hover:bg-[#fbfefe]',
      ].join(' ')}
    >
      <div className="flex items-start gap-4">
        {iconUrl ? (
          <div
            className={[
              'h-20 w-20 shrink-0 overflow-hidden rounded-full border',
              isSelected ? 'border-[#8ddfdb]' : 'border-slate-200',
            ].join(' ')}
          >
            <img src={iconUrl} alt={name} className="h-full w-full object-cover" />
          </div>
        ) : (
          <div
            className={[
              'flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full border text-2xl font-semibold',
              isSelected
                ? 'border-[#8ddfdb] text-white'
                : 'border-slate-200 bg-slate-100 text-slate-400',
            ].join(' ')}
            style={isSelected ? { backgroundColor: BRAND_TIFFANY_BLUE } : undefined}
          >
            {displayInitial}
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="truncate text-lg font-semibold">{name || '-'}</div>
              {subLabel ? (
                <div className={['mt-1 text-xs', isSelected ? 'text-slate-500' : 'text-slate-400'].join(' ')}>
                  {subLabel}
                </div>
              ) : null}
              <div
                className={[
                  'mt-3 text-sm font-medium',
                  isSelected ? 'text-[#087f7c]' : 'text-slate-600',
                ].join(' ')}
              >
                {roleName || '-'}
              </div>
            </div>

            <div className="shrink-0 pt-1">
              {isSelected ? (
                <UserRound className="h-5 w-5 text-[#087f7c]" />
              ) : (
                <ChevronRight className="h-5 w-5 text-slate-300" />
              )}
            </div>
          </div>

          <div
            className={[
              'mt-4 rounded-[24px] px-4 py-3',
              isSelected ? 'bg-[#eefaf9]' : 'bg-slate-50',
            ].join(' ')}
          >
            <div className={['text-[11px] font-semibold uppercase tracking-[0.16em]', isSelected ? 'text-[#5ea8a4]' : 'text-slate-400'].join(' ')}>
              Bio
            </div>
            <p
              className={[
                'mt-2 whitespace-pre-line break-words text-sm leading-6',
                isSelected ? 'text-slate-600' : 'text-slate-600',
              ].join(' ')}
            >
              {normalizedBio || '-'}
            </p>
          </div>

          {visibleSnsLinks.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {visibleSnsLinks.map((link, index) => {
                const normalizedPlatform = link.platform?.trim().toLowerCase();
                const socialStyle = SOCIAL_STYLE_MAP[normalizedPlatform] ?? SOCIAL_STYLE_MAP.other;
                const { Icon } = socialStyle;

                return (
                  <button
                    key={`${link.platform}-${index}`}
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      window.open(link.url, '_blank', 'noopener,noreferrer');
                    }}
                    className={[
                      'group inline-flex items-center gap-2 rounded-full border px-3 py-2 text-[11px] font-semibold shadow-sm transition',
                      socialStyle.buttonClassName,
                      isSelected ? 'ring-1 ring-white/25' : '',
                    ].join(' ')}
                    aria-label={t('common.openExternal', { label: socialStyle.label })}
                  >
                    <span
                      className={[
                        'flex h-5 w-5 items-center justify-center rounded-full',
                        socialStyle.iconWrapClassName,
                      ].join(' ')}
                    >
                      <Icon className={socialStyle.iconClassName ?? 'h-4 w-4'} />
                    </span>
                    <span>{socialStyle.label}</span>
                    <ExternalLink className="h-3.5 w-3.5 opacity-80 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default StaffSelectionCard;
